import type { PrescriptionCard } from '../api/performance.api';
import {
  getLineBadge,
  isBelowMinimumEstimate,
  isLineDisabled,
  type LineBadgeTone,
} from '../lib/performance';
import { DrugNameLink } from '@/shared/components/drug-drawer/DrugNameLink';
import { cn } from '@/shared/lib/utils';
import {
  EMPTY,
  formatNumber,
  formatYm,
  formatPercent,
  formatWon,
} from '@/shared/utils/format';
import type { ReactNode } from 'react';

/** 상태 배지 색 — Figma M-09 카드 배지 */
const TONE_CLASS: Record<LineBadgeTone, string> = {
  success: 'bg-success-soft text-success',
  primary: 'bg-primary-soft text-primary',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  muted: 'bg-muted text-ink-4',
};

interface PrescriptionLineCardProps {
  line: PrescriptionCard;
  /** 약명으로 연 M-12 드로어에 「처방월 · 병의원」 처방량을 함께 보여 준다 (M-15 C) */
  clinicName?: string;
  /** compact = M-18 뷰어(카드 368px). 제약사 배지 · 비고 · 금액 라벨을 줄인다 */
  variant?: 'default' | 'compact';
  /** 약명을 눌러 드로어를 열 때 함께 호출 (M-18: 모달을 닫고 드로어로 교체, M-15 B). capture 단계에서 닫으면 버튼이 먼저 사라져 드로어가 안 열리므로 bubble 단계에서 부른다 */
  onDrugOpen?: () => void;
}

/**
 * 처방 내역 카드 (M-09 전체 탭 · M-17 제약사 탭 · M-18 뷰어)
 * 금액은 서버 값을 그대로 쓴다. 정산 값은 같은 (처방월, 의약품) 정산 합이라 다른 카드에 반복될 수 있다.
 */
export const PrescriptionLineCard = ({
  line,
  clinicName,
  variant = 'default',
  onDrugOpen,
}: PrescriptionLineCardProps) => {
  const compact = variant === 'compact';
  const badge = getLineBadge(line);
  const disabled = isLineDisabled(line);
  const belowMin = isBelowMinimumEstimate(line);

  const badgeEl = badge && (
    <span
      className={cn(
        'inline-flex shrink-0 items-center font-medium whitespace-nowrap',
        compact
          ? 'rounded-[4px] px-1.5 py-0.5 text-[9.5px] leading-[14px]'
          : 'rounded-badge px-[7px] py-[3px] text-[10px] leading-[15px]',
        TONE_CLASS[badge.tone],
      )}
    >
      {badge.label}
    </span>
  );

  const meta = (
    <>
      <Kv label="약가" value={formatWon(line.unitPrice)} compact={compact} />
      <Dot compact={compact} />
      <Kv
        label="수수료율"
        value={formatPercent(line.feeRate)}
        compact={compact}
      />
    </>
  );

  return (
    <article
      className={cn(
        'flex min-w-0 flex-col border border-[#e8e8e8] bg-surface',
        compact
          ? 'gap-[9px] rounded-lg p-3'
          : 'gap-[13px] rounded-xl px-[18px] py-4',
        disabled && 'opacity-60',
      )}
    >
      {/* 머리: 약명 · 제약사 · 배지 · 약가/수수료율 | 상태 배지 · 비고 */}
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            compact ? 'gap-[3px]' : 'gap-1.5',
          )}
        >
          <span onClick={onDrugOpen} className="min-w-0">
            <DrugNameLink
              drugCode={line.drugCode}
              context={
                clinicName
                  ? {
                      label: [
                        line.prescriptionYearMonth &&
                          formatYm(line.prescriptionYearMonth),
                        clinicName,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                      quantity: line.outHospitalQuantity,
                    }
                  : undefined
              }
              className={cn(
                'font-bold text-ink',
                compact ? 'text-xs leading-[18px]' : 'text-[13px] leading-5',
              )}
            >
              {line.drugName ?? EMPTY}
            </DrugNameLink>
          </span>
          {compact ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[9.5px] leading-[14px] text-[#9e9e9e]">
                {line.pharmaCompanyName}
              </span>
              <Dot compact />
              {meta}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-[5px]">
                <span className="text-[10.5px] leading-4 text-ink-7">
                  {line.pharmaCompanyName}
                </span>
                {line.isReferenceDrug && (
                  <SmallBadge className="bg-primary-soft text-primary">
                    대조약
                  </SmallBadge>
                )}
                {line.isBioequivalent && (
                  <SmallBadge className="bg-success-soft text-success">
                    생동인정
                  </SmallBadge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">{meta}</div>
            </>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-[5px]">
          {badgeEl}
          {!compact && line.remarks && (
            <span className="max-w-40 text-right text-[10px] leading-[15px] text-ink-7">
              {line.remarks}
            </span>
          )}
        </div>
      </div>

      {!compact && <div className="h-px bg-[#f0f0f0]" />}

      <div className={cn('flex flex-col', compact ? 'gap-[5px]' : 'gap-1.5')}>
        <AmountRow
          kind="prescription"
          compact={compact}
          qtyLabel="처방량"
          qty={formatNumber(line.outHospitalQuantity)}
          amount={formatWon(line.outHospitalAmount)}
          totalLabel={compact ? '예상' : '예상 금액'}
          total={
            belowMin ? (
              <span
                className={cn(
                  'font-medium text-warning',
                  compact
                    ? 'text-[10px] leading-[15px]'
                    : 'text-[11px] leading-4',
                )}
              >
                최소금액 기준 미달
              </span>
            ) : (
              formatWon(line.estimatedAmount)
            )
          }
          empty={!belowMin && line.estimatedAmount === null}
        />
        <AmountRow
          kind="settlement"
          compact={compact}
          qtyLabel="인정량"
          qty={formatNumber(line.settlementQuantity)}
          amount={formatWon(line.settlementAmount)}
          totalLabel={compact ? '확정' : '확정 금액'}
          total={formatWon(line.confirmedAmount)}
          empty={line.confirmedAmount === null}
        />
      </div>
    </article>
  );
};

const SmallBadge = ({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) => (
  <span
    className={cn(
      'rounded-badge px-[7px] py-[3px] text-[10px] leading-[15px] font-medium whitespace-nowrap',
      className,
    )}
  >
    {children}
  </span>
);

const Dot = ({ compact }: { compact?: boolean }) => (
  <span
    className={cn(
      'text-[#d2d2d2]',
      compact ? 'text-[9.5px] leading-[14px]' : 'text-[10px] leading-[15px]',
    )}
  >
    ·
  </span>
);

const Kv = ({
  label,
  value,
  compact,
}: {
  label: string;
  value: ReactNode;
  compact?: boolean;
}) => (
  <span
    className={cn(
      'flex items-center whitespace-nowrap',
      compact ? 'gap-1' : 'gap-[5px]',
    )}
  >
    <span
      className={cn(
        'text-[#9a9a9a]',
        compact ? 'text-[9.5px] leading-[14px]' : 'text-[10px] leading-[15px]',
      )}
    >
      {label}
    </span>
    <span
      className={cn(
        'font-medium text-[#3f3f3f]',
        compact ? 'text-[11px] leading-4' : 'text-[11.5px] leading-[17px]',
      )}
    >
      {value}
    </span>
  </span>
);

/** 처방 / 정산 한 줄 — 처방량(인정량) · 금액 | 예상(확정) 금액 */
const AmountRow = ({
  kind,
  compact,
  qtyLabel,
  qty,
  amount,
  totalLabel,
  total,
  empty,
}: {
  kind: 'prescription' | 'settlement';
  compact: boolean;
  qtyLabel: string;
  qty: string;
  amount: string;
  totalLabel: string;
  total: ReactNode;
  empty: boolean;
}) => {
  const isRx = kind === 'prescription';
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-1',
        isRx ? 'bg-[#f5f8fd]' : 'bg-[#f3f9f6]',
        compact
          ? 'rounded-sm py-[7px] pr-2.5 pl-[9px]'
          : 'rounded-md py-[9px] pr-3 pl-2.5',
      )}
    >
      <div
        className={cn(
          'flex min-w-0 items-center',
          compact ? 'gap-[9px]' : 'gap-3',
        )}
      >
        <span
          className={cn(
            'shrink-0 font-medium whitespace-nowrap',
            isRx
              ? 'bg-[#e4ebf8] text-[#4a63a8]'
              : 'bg-[#dcf0e6] text-[#2f7f5e]',
            compact
              ? 'rounded-[4px] px-1.5 py-0.5 text-[9.5px] leading-[14px]'
              : 'rounded-badge px-[7px] py-[3px] text-[10px] leading-[15px]',
          )}
        >
          {isRx ? '처방' : '정산'}
        </span>
        <Kv label={qtyLabel} value={qty} compact={compact} />
        {compact ? (
          <span className="text-[11px] leading-4 font-medium whitespace-nowrap text-[#3f3f3f]">
            {amount}
          </span>
        ) : (
          <Kv label="금액" value={amount} />
        )}
      </div>
      <div
        className={cn(
          'ml-auto flex items-center whitespace-nowrap',
          compact ? 'gap-[5px]' : 'gap-1.5',
        )}
      >
        <span
          className={cn(
            'text-[#8a8a8a]',
            compact
              ? 'text-[9.5px] leading-[14px]'
              : 'text-[10px] leading-[15px]',
          )}
        >
          {totalLabel}
        </span>
        <span
          className={cn(
            'font-bold',
            compact ? 'text-[12.5px] leading-[19px]' : 'text-sm leading-[21px]',
            empty
              ? 'font-normal text-ink-8'
              : isRx
                ? 'text-ink'
                : 'text-success',
          )}
        >
          {total}
        </span>
      </div>
    </div>
  );
};
